/**
 * Video Controller
 * Manages video loading, caching, and playback
 */

import { saveVideo, getVideo } from '../utils/indexeddb.js';
import { isSlowNetwork, getAdaptiveHLSConfig } from '../utils/network-speed.js';
import Hls from 'hls.js';
import { trackVideoEnded, trackVideoProgress } from '../utils/analytics.js';

const PROGRESS_THRESHOLDS = [25, 50, 75];

/**
 * Video Controller class for video playback management
 */
export class VideoController {
  constructor() {
    this.videoElement = null;
    this.currentVideoId = null;
    this.isPlaying = false;
    this.objectURL = null;
    this.hlsInstance = null; // HLS.js instance for Cloudflare Stream
    this._analyticsAttached = false; // 進捗計測フックの多重登録防止
    this.deliveryType = null; // 'hls' or 'mp4'
    this.loopEnabled = true; // ループ再生の有効/無効(デフォルト有効、posterData.videoLoop=false で opt-out)
    this._isNativeHLS = false; // ネイティブ HLS 再生経路か (iOS Safari など)。#38 の loop 分岐に使用
  }

  /**
   * Initialize with video element
   * @param {HTMLVideoElement} videoElement - Video element for playback
   * @param {Object} [options]
   * @param {boolean} [options.loop=true] - ループ再生の有効/無効
   */
  init(videoElement, { loop = true } = {}) {
    this.videoElement = videoElement;
    this.loopEnabled = loop;

    // Configure video element
    this.videoElement.setAttribute('playsinline', ''); // iOS requirement
    this.videoElement.setAttribute('webkit-playsinline', '');
    this.videoElement.setAttribute('crossorigin', 'anonymous');

    // 再生経路の判定: iOS Safari 系はネイティブ HLS、それ以外は hls.js/MSE または MP4 直配信
    this._isNativeHLS = !!this.videoElement.canPlayType?.('application/vnd.apple.mpegurl');

    // #31: iOS Safari の native HLS 再生は <video loop> 属性を信頼せず、動画終端で pause して
    // ended も発火しない (video_ended=0)。この経路だけ loop=false 固定 + ended リスナーの手動再開に切り替える。
    // #38: 上の対策を全経路に適用すると、hls.js/MSE 経路 (Android Chrome 等) で endOfStream() と
    // 手動 play() が競合して silent fail する。非ネイティブ HLS 経路では native `<video loop>` に
    // ループ制御を委ねる (これは #31 以前の実装で正常動作していた挙動)。
    this.videoElement.loop = this._isNativeHLS ? false : this.loopEnabled;
    this.videoElement.muted = true; // Must be muted for autoplay policy

    console.log(
      `VideoController initialized (loopEnabled=${this.loopEnabled}, nativeHLS=${this._isNativeHLS}, videoElement.loop=${this.videoElement.loop})`
    );
  }

  /**
   * Load video from URL with IndexedDB caching or Cloudflare Stream HLS
   * @param {string} videoId - Video ID for caching
   * @param {string} videoUrl - URL to video file (MP4 fallback)
   * @param {Function} onProgress - Optional progress callback (percent) => void
   * @param {string} lowQualityUrl - Optional low quality fallback URL
   * @param {Object} streamConfig - Optional Cloudflare Stream config { streamVideoId, manifestUrl }
   * @returns {Promise<void>}
   */
  async load(videoId, videoUrl, onProgress = null, lowQualityUrl = null, streamConfig = null) {
    // R2 進捗計測フックはこの videoId で1回だけ張る（同一 controller の再load は想定しない）
    this._attachAnalyticsHooks(videoId);

    try {
      // Cloudflare Stream HLS配信
      if (streamConfig?.streamVideoId) {
        console.log(`📺 Loading Cloudflare Stream HLS: ${videoId}`);
        await this._loadHLS(videoId, streamConfig, videoUrl, onProgress);
        return;
      }

      // 通常のMP4配信（既存ロジック）
      // Auto-select quality based on network speed
      let selectedUrl = videoUrl;

      if (lowQualityUrl && isSlowNetwork()) {
        console.log(`📡 Slow network detected, using low quality video`);
        selectedUrl = lowQualityUrl;
      }

      console.log(`Loading video ${videoId} from ${selectedUrl}`);

      // Check IndexedDB cache first
      const cachedVideo = await getVideo(videoId);

      let blob;
      if (cachedVideo && cachedVideo.blob) {
        console.log(`Video ${videoId} loaded from IndexedDB cache`);
        blob = cachedVideo.blob;
        if (onProgress) onProgress(100);
      } else {
        // Download video with progress tracking
        console.log(`Downloading video ${videoId}...`);
        const response = await fetch(selectedUrl);

        if (!response.ok) {
          throw new Error(`Failed to download video: ${response.status}`);
        }

        const contentLength = response.headers.get('content-length');
        if (!contentLength || !onProgress) {
          // No progress tracking
          blob = await response.blob();
        } else {
          // Track download progress
          const total = parseInt(contentLength, 10);
          let loaded = 0;

          const reader = response.body.getReader();
          const chunks = [];

          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            chunks.push(value);
            loaded += value.length;

            if (onProgress) {
              const percent = (loaded / total) * 100;
              onProgress(percent);
            }
          }

          blob = new Blob(chunks);
        }

        console.log(`Video ${videoId} downloaded (${blob.size} bytes)`);

        // Cache in IndexedDB (async, don't wait)
        saveVideo(videoId, blob, { url: selectedUrl, cachedAt: Date.now() }).catch((error) => {
          console.warn('Failed to cache video in IndexedDB:', error);
        });
      }

      // Revoke previous object URL if exists
      if (this.objectURL) {
        URL.revokeObjectURL(this.objectURL);
      }

      // Create object URL for video
      this.objectURL = URL.createObjectURL(blob);
      this.videoElement.src = this.objectURL;

      // Wait for video to be ready with longer timeout for slow networks
      const timeoutDuration = isSlowNetwork() ? 90000 : 60000; // 90s for slow, 60s for normal
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error(`Video load timeout after ${timeoutDuration / 1000} seconds`));
        }, timeoutDuration);

        this.videoElement.onloadeddata = () => {
          clearTimeout(timeout);
          console.log(`Video ${videoId} ready to play`);
          console.log(`Duration: ${this.videoElement.duration}s`);
          resolve();
        };

        this.videoElement.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Failed to load video element'));
        };

        // Also listen for loadedmetadata as fallback
        this.videoElement.onloadedmetadata = () => {
          clearTimeout(timeout);
          console.log(`Video ${videoId} metadata loaded`);
          resolve();
        };
      });

      this.currentVideoId = videoId;
    } catch (error) {
      console.error(`Failed to load video ${videoId}:`, error);
      throw error;
    }
  }

  /**
   * Play video
   * @returns {Promise<void>}
   */
  async play() {
    if (!this.videoElement) {
      throw new Error('VideoController not initialized');
    }

    try {
      await this.videoElement.play();
      this.isPlaying = true;
      console.log('Video playback started');
    } catch (error) {
      console.error('Failed to play video:', error);
      throw error;
    }
  }

  /**
   * Pause video
   */
  pause() {
    if (!this.videoElement) {
      return;
    }

    this.videoElement.pause();
    this.isPlaying = false;
    console.log('Video playback paused');
  }

  /**
   * 進捗トラッキング用リスナーを video 要素に一度だけ張る。
   * - play: 0% を1回
   * - timeupdate: 25/50/75% 通過を各1回。全閾値到達後は早期return
   * - ended: 完視聴を1回
   * 呼び出しは load() 内から自動で行う。
   * @private
   */
  _attachAnalyticsHooks(videoId) {
    if (this._analyticsAttached || !this.videoElement) return;
    this._analyticsAttached = true;

    const el = this.videoElement;
    const emitted = new Set();
    // video_ended は他計測(0/25/50/75)と別軸のガード。emitted Set に混ぜると
    // `emitted.size > PROGRESS_THRESHOLDS.length` の早期return条件が壊れて閾値取り漏れが起きるため、
    // 独立フラグで持つ。手動ループのたびに ended が再発火しても R2 送信は1セッション1回に絞る。
    let endedEmitted = false;

    el.addEventListener('play', () => {
      if (emitted.has(0)) return;
      emitted.add(0);
      trackVideoProgress(videoId, 0);
    });

    el.addEventListener('timeupdate', () => {
      if (emitted.size > PROGRESS_THRESHOLDS.length) return; // 0 + 25/50/75 全部済み
      const duration = el.duration;
      if (!duration || !isFinite(duration) || duration <= 0) return;
      const pct = (el.currentTime / duration) * 100;
      for (const threshold of PROGRESS_THRESHOLDS) {
        if (pct >= threshold && !emitted.has(threshold)) {
          emitted.add(threshold);
          trackVideoProgress(videoId, threshold);
        }
      }
    });

    el.addEventListener('ended', () => {
      if (!endedEmitted) {
        endedEmitted = true;
        trackVideoEnded(videoId);
      }
      // #38: ネイティブ HLS 経路 (iOS Safari) だけ loop=false 固定なので、ended 到達後に自力で
      // currentTime=0 して再生する。他経路 (hls.js/MSE, MP4直配信) は init() で native `<video loop>`
      // を有効化しているので手動再開は不要 (むしろ hls.js の endOfStream と競合して silent fail する)。
      // isPlaying=false のとき (マーカーロストで pause 済み) は再開しない — マーカー画角外での勝手再生防止。
      // トラッキング送信は上のガードで1回きり、ループ動作自体は毎回実行する (見た目の連続性のため)。
      if (this._isNativeHLS && this.loopEnabled && this.isPlaying) {
        el.currentTime = 0;
        el.play().catch((err) => {
          console.error(`Failed to restart video ${videoId}:`, err);
        });
      }
    });
  }

  /**
   * Stop video and reset
   */
  stop() {
    if (!this.videoElement) {
      return;
    }

    this.videoElement.pause();
    this.videoElement.currentTime = 0;
    this.isPlaying = false;
    console.log('Video playback stopped');
  }

  /**
   * Get video element
   * @returns {HTMLVideoElement}
   */
  getVideoElement() {
    return this.videoElement;
  }

  /**
   * Check if video is playing
   * @returns {boolean}
   */
  getIsPlaying() {
    return this.isPlaying;
  }

  /**
   * Get current video ID
   * @returns {string|null}
   */
  getCurrentVideoId() {
    return this.currentVideoId;
  }

  /**
   * Set volume
   * @param {number} volume - Volume (0.0 to 1.0)
   */
  setVolume(volume) {
    if (this.videoElement) {
      this.videoElement.volume = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Mute/unmute video
   * @param {boolean} muted - True to mute
   */
  setMuted(muted) {
    if (this.videoElement) {
      this.videoElement.muted = muted;
      console.log(`🔊 Video muted: ${muted}`);
    }
  }

  /**
   * Load video from Cloudflare Stream HLS
   * @param {string} videoId - Video ID
   * @param {Object} streamConfig - Stream config { streamVideoId, manifestUrl }
   * @param {string} fallbackUrl - MP4 fallback URL
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<void>}
   * @private
   */
  async _loadHLS(videoId, streamConfig, fallbackUrl, onProgress = null) {
    this.deliveryType = 'hls';

    // Build manifest URL
    const manifestUrl =
      streamConfig.manifestUrl ||
      `https://cloudflarestream.com/${streamConfig.streamVideoId}/manifest/video.m3u8`;

    console.log(`📺 HLS manifest URL: ${manifestUrl}`);

    // iOS Safari: ネイティブHLS対応
    if (this.videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      console.log(`🍎 Using native HLS for ${videoId}`);
      this.videoElement.src = manifestUrl;
      this.videoElement.load();

      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('HLS load timeout after 60 seconds'));
        }, 60000);

        this.videoElement.addEventListener(
          'canplay',
          () => {
            clearTimeout(timeout);
            console.log(`✅ iOS HLS video ready: ${videoId}`);
            if (onProgress) onProgress(100);
            resolve();
          },
          { once: true }
        );

        this.videoElement.onerror = () => {
          clearTimeout(timeout);
          console.error('❌ Native HLS failed, trying fallback MP4');
          // Try fallback MP4
          this._loadMP4Fallback(videoId, fallbackUrl, onProgress).then(resolve).catch(reject);
        };
      });

      // iOS用: 再生エンジン事前準備
      // DISABLED: iOS動画揺れ問題のため無効化
      // タイムラグ削減よりも揺れ解消を優先
      // try {
      //   await this.videoElement.play();
      //   await new Promise(resolve => setTimeout(resolve, 100));
      //   this.videoElement.pause();
      //   console.log(`🎬 Video playback engine prepared: ${videoId}`);
      // } catch (error) {
      //   console.warn(`⚠️ Failed to prepare playback: ${error.message}`);
      // }
    }
    // その他ブラウザ: HLS.js
    else if (Hls.isSupported()) {
      console.log(`📡 Using HLS.js for ${videoId}`);

      // Get adaptive HLS config based on network speed
      const hlsConfig = getAdaptiveHLSConfig();
      console.log(`📡 HLS config: ${hlsConfig.maxBufferLength}s buffer`);

      const hls = new Hls(hlsConfig);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log(`✅ HLS manifest parsed for ${videoId}`);
        console.log(
          `📊 Available quality levels: ${hls.levels.map((l) => `${l.height}p`).join(', ')}`
        );
        if (onProgress) onProgress(100);
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error(`❌ Fatal HLS error for ${videoId}:`, data);
          hls.destroy();
          // Try fallback MP4
          this._loadMP4Fallback(videoId, fallbackUrl, onProgress);
        }
      });

      hls.loadSource(manifestUrl);
      hls.attachMedia(this.videoElement);
      this.hlsInstance = hls;

      // Wait for manifest to be parsed
      await new Promise((resolve, reject) => {
        hls.on(Hls.Events.MANIFEST_PARSED, resolve);
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) reject(new Error(`HLS error: ${data.details}`));
        });
        setTimeout(() => reject(new Error('HLS manifest parse timeout')), 60000);
      });
    }
    // HLS非対応: MP4フォールバック
    else {
      console.warn(`⚠️ HLS not supported, using MP4 fallback for ${videoId}`);
      await this._loadMP4Fallback(videoId, fallbackUrl, onProgress);
    }

    this.currentVideoId = videoId;
  }

  /**
   * Load MP4 fallback (used when HLS fails or not supported)
   * @param {string} videoId - Video ID
   * @param {string} videoUrl - MP4 URL
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<void>}
   * @private
   */
  async _loadMP4Fallback(videoId, videoUrl, onProgress = null) {
    this.deliveryType = 'mp4';
    console.log(`📹 Loading MP4 fallback: ${videoId} from ${videoUrl}`);

    this.videoElement.src = videoUrl;

    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('MP4 fallback load timeout'));
      }, 60000);

      this.videoElement.onloadeddata = () => {
        clearTimeout(timeout);
        console.log(`✅ MP4 fallback loaded: ${videoId}`);
        if (onProgress) onProgress(100);
        resolve();
      };

      this.videoElement.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Failed to load MP4 fallback'));
      };
    });
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.stop();

    // Cleanup HLS instance
    if (this.hlsInstance) {
      this.hlsInstance.destroy();
      this.hlsInstance = null;
      console.log('HLS instance destroyed');
    }

    // Revoke object URL
    if (this.objectURL) {
      URL.revokeObjectURL(this.objectURL);
      this.objectURL = null;
    }

    // Clear video source
    if (this.videoElement) {
      this.videoElement.src = '';
      this.videoElement.load();
    }

    this.currentVideoId = null;
    this.deliveryType = null;
    console.log('VideoController cleaned up');
  }
}
